import {ComponentType} from '@angular/cdk/overlay';
import {AUTOUNSUBSCRIBE_LISTENERS, SUBSCRIPTIONS} from '../common/utils/constants';
import {Subscription} from 'rxjs';
import {AutoUnsubscribeHelperService} from '../services/auto-unsubscribe-helper.service';

type ConstructorFunction = abstract new (...args: any[]) => any;
type HandlerData = EventListener | [Element | Document, () => void];

/*
 * AutoUnsubscribe decorator automatically unsubscribes from subscriptions and removes
 * event listeners on component destruction.
 * @param skip An array of property names to skip during the auto-unsubscribe process.
 * @description Usage: Add the decorator to any Angular component to
 * automatically unsubscribe from subscriptions and remove event listeners on component destruction.
 * @description In cases when you need to unsubscribe subscription on destroy - you don't need to create variable
 * for this subscription - just add this code:
 * @example
 * this[SUBSCRIPTIONS].add(your subscription logic), and this subscription will unsubscribe on component destruction.
 *
 * If you want to use variable for subscription in your component - skip variable check by adding:
 * @example
 * // '/* eslint-disable-next-line local/no-subscription-variables or eslint-disable local/no-subscription-variables'
 *
 * To remove event listeners on component destruction - set AUTOUNSUBSCRIBE_LISTENERS in a component.
 * @example
 * htmlElement.addEventListener('click', Your callback function);
 * this[AUTOUNSUBSCRIBE_LISTENERS].set('click', [htmlElement, Your callback function])
 *
 * Or if you add eventListener on document - you can simplify call
 * @example
 * document.addEventListener('click', Your callback function);
 * this[AUTOUNSUBSCRIBE_LISTENERS].set('click', Your callback function)
 */
export function AutoUnsubscribe<T>(skip: string[] = []): (constructor: ComponentType<T> | ConstructorFunction) => void {
  return constructor => {
    const autoUnsubscribeHelperService: AutoUnsubscribeHelperService = new AutoUnsubscribeHelperService();
    const componentName: string = constructor.name;
    const originalOnDestroy = constructor.prototype.ngOnDestroy;
    const originalOnInit = constructor.prototype.ngOnInit;
    constructor.prototype[AUTOUNSUBSCRIBE_LISTENERS] = new Map<string, [Element | Document, () => void]>();
    constructor.prototype[SUBSCRIPTIONS] = new Subscription();
    constructor.prototype.ngOnInit = function (): void {
      autoUnsubscribeHelperService.setComponentInstances(componentName);
      if (originalOnInit && typeof originalOnInit === 'function') {
        originalOnInit.apply(this, arguments);
      }
    };
    constructor.prototype.ngOnDestroy = function (): void {
      Object.keys(this).forEach(key => {
        if (!skip.includes(key)) {
          const property = this[key];

          if (property && typeof property.unsubscribe === 'function') {
            property.unsubscribe();
          }
        }
      });

      if (originalOnDestroy && typeof originalOnDestroy === 'function') {
        originalOnDestroy.apply(this, arguments);
      }

      this[AUTOUNSUBSCRIBE_LISTENERS].forEach((handler: HandlerData, eventName: string) => {
        if (handler instanceof Function) {
          document.removeEventListener(eventName, handler);
        } else if (handler instanceof Array) {
          const [eventElement, eventCallback] = handler;
          eventElement.removeEventListener(eventName, eventCallback);
        }
      });

      if (autoUnsubscribeHelperService.checkIfNotMultiComponentInstances(componentName)) {
        (this[SUBSCRIPTIONS] as Subscription).unsubscribe();
      }

      autoUnsubscribeHelperService.clearComponentInstances(componentName);

      constructor.prototype[SUBSCRIPTIONS] = new Subscription();
    };
  };
}
